// components/pages/SponsorInfo.js
// Public "How sponsorship works" explainer page. Lives at /sponsor and links to the
// application form at /sponsor/apply. Follows the legal-page layout conventions.
import React, { useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate, Link } from 'react-router-dom';
import Container from '../common/Container';
import { SectionTitle, SectionSubtitle } from '../common/SharedStyles';
import Button from '../common/Button';

const Section = styled.section`
  padding: 6rem 0;
  background-color: var(--background-primary);
`;
const NarrowContainer = styled(Container)`
  max-width: 820px;
`;
const Steps = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.25rem;
  margin: 2.5rem 0;
  @media (max-width: 640px) { grid-template-columns: 1fr; }
`;
const Step = styled.div`
  background: var(--background-secondary);
  border: 1px solid var(--border-color);
  border-radius: 10px;
  padding: 1.5rem;
`;
const StepNumber = styled.div`
  width: 34px; height: 34px; border-radius: 50%;
  background: var(--accent-primary); color: var(--background-primary);
  display: flex; align-items: center; justify-content: center;
  font-weight: 800; margin-bottom: 0.75rem;
`;
const StepTitle = styled.h3`
  margin: 0 0 0.4rem; font-size: 1.1rem; color: var(--text-primary);
`;
const StepText = styled.p`
  margin: 0; color: var(--text-secondary); line-height: 1.5; font-size: 0.95rem;
`;
const Highlight = styled.div`
  background: rgba(240, 180, 50, 0.1);
  border: 1px solid rgba(240, 180, 50, 0.35);
  border-radius: 10px;
  padding: 1.5rem;
  text-align: center;
  margin: 2rem 0;
`;
const Price = styled.div`
  font-size: 2.2rem; font-weight: 800; color: var(--accent-primary);
`;
const CtaRow = styled.div`
  display: flex; gap: 1rem; justify-content: center; flex-wrap: wrap; margin-top: 2rem;
`;
const FaqTitle = styled.h2`
  color: var(--accent-secondary); margin-top: 2.5rem; font-size: 1.4rem;
`;
const FaqQ = styled.h4`margin: 1.25rem 0 0.25rem; color: var(--text-primary);`;
const FaqA = styled.p`margin: 0; color: var(--text-secondary); line-height: 1.6;`;
const PortalNote = styled.p`
  color: var(--text-secondary); text-align: center; margin-top: 2rem; font-size: 0.9rem;
`;

const steps = [
  { t: 'Submit your details', d: 'Tell us your company name, contact info, destination link, and upload your logo — takes a minute.' },
  { t: 'We review it', d: "Our team reviews every submission. You'll get an email when it's approved (or with feedback if changes are needed)." },
  { t: 'Start your subscription', d: 'Once approved, log in to your private portal and start your $100 / 30-day subscription.' },
  { t: 'Go live', d: 'Your logo appears in the Sivo app sidebar for all users. Clicking it opens your website in a new tab.' },
  { t: 'Track performance', d: 'See impressions, clicks, click-through rate, and regions right in your portal — updated live.' },
  { t: 'Auto-renew or cancel', d: 'Your sponsorship auto-renews every 30 days. Cancel any time — your sponsorship is showcased until the end of the 30 days.' },
];

const SponsorInfo = () => {
  const navigate = useNavigate();
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <Section id="sponsor-info">
      <NarrowContainer>
        <SectionTitle>Become a Sivo Sponsor</SectionTitle>
        <SectionSubtitle>
          Put your company's logo — linked directly to your website — in front of sign
          manufacturers who use Sivo every day. Simple, transparent, and fully self-service.
        </SectionSubtitle>

        <Highlight>
          <Price>$100</Price>
          <div style={{ color: 'var(--text-secondary)' }}>per 30 days, auto-renewing • cancel anytime</div>
        </Highlight>

        <Steps>
          {steps.map((s, i) => (
            <Step key={i}>
              <StepNumber>{i + 1}</StepNumber>
              <StepTitle>{s.t}</StepTitle>
              <StepText>{s.d}</StepText>
            </Step>
          ))}
        </Steps>

        <CtaRow>
          <Button variant="primary" onClick={() => navigate('/sponsor/apply')}>Apply to sponsor</Button>
          <Button variant="outline" onClick={() => navigate('/')}>Back to home</Button>
        </CtaRow>

        <FaqTitle>Frequently asked questions</FaqTitle>

        <FaqQ>How much does it cost?</FaqQ>
        <FaqA>$100 for a 30-day period. It renews automatically every 30 days until you cancel.</FaqA>

        <FaqQ>When does my logo appear?</FaqQ>
        <FaqA>As soon as your payment is processed after approval. Payment only becomes available once an admin approves your submission.</FaqA>

        <FaqQ>Can I change my logo or link later?</FaqQ>
        <FaqA>Yes — edit anytime from your portal. Edits go back to us for a quick review; your current logo keeps showing until the change is approved, and your billing cycle doesn't change.</FaqA>

        <FaqQ>What happens if I cancel?</FaqQ>
        <FaqA>Your sponsorship stays showcased until the current period ends. Your performance history is kept, so you can pick up where you left off if you come back.</FaqA>

        <FaqQ>What metrics do I get?</FaqQ>
        <FaqA>Impressions, clicks, click-through rate, a breakdown of clicks by region, and your next renewal date.</FaqA>

        <PortalNote>
          Already a sponsor? Use the private portal link we emailed you. Ready to start?{' '}
          <Link to="/sponsor/apply">Apply now</Link>.
        </PortalNote>
      </NarrowContainer>
    </Section>
  );
};

export default SponsorInfo;
